import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gavel, Send, Loader2, CheckCircle2, Clock, FileEdit, Plus } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DecisionEditor } from "./DecisionEditor";
import { archiveSentDecision } from "../lib/decisionArchive";

const ST = {
  draft: ["مسودة — لم تُرسل للموظف", "bg-slate-100 text-slate-600", FileEdit],
  sent: ["أُرسل — بانتظار التوقيع", "bg-amber-50 text-amber-700", Clock],
  signed: ["وقّعه الموظف", "bg-emerald-50 text-emerald-700", CheckCircle2],
};

export const EmployeeDecisions = ({ employeeId, employee, canEdit = true }) => {
  const { company } = useAuth();
  const [edit, setEdit] = useState(null);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.employeeDecisions(employeeId).then((r) => setItems(r.items || [])).catch(() => setItems([])), [employeeId]);
  useEffect(() => { load(); }, [load]);
  const send = async (d) => {
    setBusy(d.id);
    try {
      await api.sendDecision(d.id);
      try { await archiveSentDecision({ ...d, employee_name: d.employee_name || employee?.name }, company); toast.success("تم إرسال القرار وحُفظ في مكتبة الشركة وملف الموظف ✅"); }
      catch { toast.warning("أُرسل القرار، لكن تعذّر حفظ نسخته في المكتبة"); }
      load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); } finally { setBusy(""); }
  };
  if (items === null) return <p className="text-sm text-slate-400">جارٍ التحميل…</p>;
  return (
    <div className="space-y-2" data-testid="emp-decisions">
      {canEdit && employee && (
        <div className="flex justify-end">
          <Button size="sm" className="gap-1.5" onClick={() => setEdit({ employee_id: employee.id })} data-testid="emp-decision-new"><Plus className="h-4 w-4" /> قرار جديد</Button>
        </div>
      )}
      {!items.length && <p className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="emp-decisions-empty">لا توجد قرارات لهذا الموظف</p>}
      {edit && <DecisionEditor open onOpenChange={(o) => !o && setEdit(null)} decision={edit} employees={[employee]} onSaved={load} />}
      {items.map((d) => {
        const [label, cls, Icon] = ST[d.status] || ST.draft;
        return (
          <div key={d.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid={`emp-decision-${d.id}`}>
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-indigo-50 text-indigo-600"><Gavel className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-slate-800">{d.title}</p>
              <p className="text-xs text-slate-500">رقم {d.number} · {d.date}{d.signed_at ? ` · وُقّع ${d.signed_at.slice(0, 10)}` : ""}</p>
            </div>
            <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}><Icon className="h-3 w-3" /> {label}</span>
            {d.status !== "signed" && (
              <Button size="sm" variant={d.status === "draft" ? "default" : "outline"} disabled={busy === d.id} onClick={() => send(d)} className="gap-1" data-testid={`emp-decision-send-${d.id}`}>
                {busy === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {d.status === "draft" ? "إرسال للموظف" : "إعادة إرسال"}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};
